import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@material-ui/core";
import { DateTimePicker, MaterialUiPickersDate } from "@material-ui/pickers";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";

import { AjaxResult } from "../../../enums/ajaxResult";
import { FormSection } from "../../shared/formSection";
import { FormField } from "../../shared/formField";
import { TextField } from "../../shared/TextField";
import { useApi } from "../../useApi";
import { MaintenanceModeDto } from "../../../dtos/site";

interface Props {
    open: boolean;
    onClose: () => void;
    siteId: number;
    onSuccess: () => void;
    mode?: 'enable' | 'extend';
    currentEndTime?: Date;
}

export function EnableMaintenanceModeDialog({
    open,
    onClose,
    siteId,
    onSuccess,
    mode = 'enable',
    currentEndTime,
}: Props) {
    const { maintenanceModeApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);

    const [endTime, setEndTime] = useState<Date | null>(
        mode === 'extend' && currentEndTime
            ? moment(currentEndTime).add(1, 'hour').toDate()
            : moment().add(1, 'hour').toDate()
    );
    const [comment, setComment] = useState("");
    const [errors, setErrors] = useState<{ endTime?: string; comment?: string }>({});

    useEffect(() => {
        if (open) {
            if (mode === 'enable') {
                setEndTime(moment().add(1, 'hour').toDate());
            } else if (mode === 'extend' && currentEndTime) {
                setEndTime(moment(currentEndTime).add(1, 'hour').toDate());
            }
            setComment("");
            setErrors({});
        }
    }, [open, mode, currentEndTime]);

    const validate = useCallback((): boolean => {
        const newErrors: typeof errors = {};

        if (!endTime) {
            newErrors.endTime = "End time is required";
        } else if (endTime <= new Date()) {
            newErrors.endTime = "End time must be in the future";
        }

        if (comment && comment.length > 1000) {
            newErrors.comment = "Comment must be 1000 characters or less";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [endTime, comment]);

    const getDuration = useCallback((): string => {
        if (!endTime) return "";

        const now = moment();
        const duration = moment.duration(moment(endTime).diff(now));
        const hours = Math.floor(duration.asHours());
        const minutes = duration.minutes();

        if (hours === 0) {
            return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
        } else if (minutes === 0) {
            return `${hours} hour${hours !== 1 ? 's' : ''}`;
        } else {
            return `${hours} hour${hours !== 1 ? 's' : ''}, ${minutes} minute${minutes !== 1 ? 's' : ''}`;
        }
    }, [endTime]);

    const handleEndTimeChange = useCallback((date: MaterialUiPickersDate) => {
        setEndTime(date && date.toDate());
        setErrors(prev => ({ ...prev, endTime: undefined }));
    }, []);

    const handleCommentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setComment(e.target.value);
        setErrors(prev => ({ ...prev, comment: undefined }));
    }, []);

    const handleSave = useCallback(() => {
        if (!validate()) {
            return;
        }

        setLoading(true);

        if (mode === 'enable') {
            const dto: MaintenanceModeDto = {
                siteId: siteId,
                startTime: moment.utc().toISOString(),
                endTime: endTime!.toISOString(),
                comment: comment || undefined,
            };

            maintenanceModeApi.enable(siteId, dto).then((r) => {
                if (r.result === AjaxResult.Success) {
                    enqueueSnackbar(
                        "Maintenance mode enabled successfully",
                        { variant: "success" }
                    );
                    onSuccess();
                    onClose();
                } else {
                    const errorMsg = r.messages && r.messages.length > 0
                        ? r.messages.join(", ")
                        : "Failed to enable maintenance mode";
                    enqueueSnackbar(errorMsg, { variant: "error" });
                }
                setLoading(false);
            });
        } else {
            maintenanceModeApi.extend(siteId, endTime!.toISOString()).then((r) => {
                if (r.result === AjaxResult.Success) {
                    enqueueSnackbar(
                        "Maintenance mode extended successfully",
                        { variant: "success" }
                    );
                    onSuccess();
                    onClose();
                } else {
                    const errorMsg = r.messages && r.messages.length > 0
                        ? r.messages.join(", ")
                        : "Failed to extend maintenance mode";
                    enqueueSnackbar(errorMsg, { variant: "error" });
                }
                setLoading(false);
            });
        }
    }, [validate, mode, siteId, endTime, comment, maintenanceModeApi, enqueueSnackbar, onSuccess, onClose]);

    const title = mode === 'enable' ? 'Enable Maintenance Mode' : 'Extend Maintenance Mode';
    const saveButtonText = mode === 'enable' ? 'Enable' : 'Extend';
    const savingButtonText = mode === 'enable' ? 'Enabling...' : 'Extending...';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <FormSection headerText={mode === 'enable' ? 'Schedule Maintenance Period' : 'Extend Maintenance Period'}>
                    <FormField label="End Time" required>
                        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            <DateTimePicker
                                value={endTime}
                                onChange={handleEndTimeChange}
                                format="MM/DD/YYYY hh:mm A"
                                inputVariant="outlined"
                                fullWidth
                                minDate={new Date()}
                            />
                            {errors.endTime && (
                                <Typography color="error" variant="caption">
                                    {errors.endTime}
                                </Typography>
                            )}
                        </div>
                    </FormField>

                    {endTime && !errors.endTime && (
                        <FormField label="Duration">
                            <Typography variant="body2" style={{ padding: '8px 0' }}>
                                {getDuration()}
                            </Typography>
                        </FormField>
                    )}

                    {mode === 'enable' && (
                        <div style={{ margin: '5px 0' }}>
                            <label style={{ display: 'block', marginBottom: '4px' }}>Comment (Optional):</label>
                            <TextField
                                placeholder="Optional comment about this maintenance period"
                                value={comment}
                                onChange={handleCommentChange}
                                variant="outlined"
                                multiline
                                rows={3}
                                fullWidth
                                InputProps={{ style: { display: 'flex' } }}
                            />
                            {errors.comment && (
                                <Typography color="error" variant="caption">
                                    {errors.comment}
                                </Typography>
                            )}
                            <Typography variant="caption" color="textSecondary">
                                {comment.length}/1000 characters
                            </Typography>
                        </div>
                    )}
                </FormSection>
            </DialogContent>

            <DialogActions>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={loading || !endTime}
                >
                    {loading ? savingButtonText : saveButtonText}
                </Button>

                <Button
                    variant="contained"
                    color="secondary"
                    onClick={onClose}
                    disabled={loading}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
